import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, RHFControl } from 'components/common/form';
import { Col, Row } from 'react-bootstrap';
import LoadingButton from 'components/common/LoadingButton';
import { toast } from 'react-toastify';
import { authHelpers } from 'lib/supabase';
import { useAuthContext } from 'context/authContext';
import { useState } from 'react';

const schema = Yup.object().shape({
  password: Yup.string().required('Current password is required'),
  newPassword: Yup.string()
    .required('New password is required')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
      'Must contain 8 characters, one uppercase, one lowercase, one number'
    )
    .min(8, 'Must be at least 8 characters'),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
});

const changePasswordResolver = yupResolver(schema);

export default function ChangePassword() {
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(false);

  const methods = useForm({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      password: '',
      newPassword: '',
      confirmPassword: ''
    },
    resolver: changePasswordResolver
  });

  const { handleSubmit } = methods;

  const onSubmit = async data => {
    try {
      setLoading(true);
      const { success, error } = await authHelpers.changePassword(
        user.email,
        data.password,
        data.newPassword
      );

      if (!success || error) {
        toast.error(error?.message || 'Failed to change password');
        return;
      }

      toast.success('Password changed successfully');
      methods.reset();
    } catch (error) {
      console.error('Change password error:', error);
      toast.error('An error occurred while changing password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <div className="d-flex flex-column gap-3">
        <Row>
          <Col md={6}>
            <RHFControl
              label="Current Password"
              name="password"
              placeholder="Enter current password"
              type="password"
              required
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <RHFControl
              label="New Password"
              name="newPassword"
              placeholder="Enter new password"
              type="password"
              required
            />
          </Col>
          <Col md={6}>
            <RHFControl
              label="Confirm Password"
              name="confirmPassword"
              placeholder="Confirm new password"
              type="password"
              required
            />
          </Col>
        </Row>

        <LoadingButton loading={loading} type="submit" className="mb-2">
          Change Password
        </LoadingButton>
      </div>
    </FormProvider>
  );
}
